import React from 'react';
import { AlertCircleIcon } from 'lucide-react';

interface BaseProps {
  id: string;
  label: string;
  hint?: string;
  error?: string;
  required?: boolean;
  children?: React.ReactNode;
}

export function Field({ id, label, hint, error, required, children }: BaseProps) {
  return (
    <div className="field">
      <label className="field-label" htmlFor={id}>
        {label}
        {required ? <span className="req" aria-hidden="true">*</span> : null}
      </label>
      {hint ?
      <p className="field-hint" id={`${id}-hint`}>
          {hint}
        </p> :
      null}
      {children}
      {error ?
      <p className="error-text" id={`${id}-error`} role="alert">
          <AlertCircleIcon size={13} aria-hidden="true" />
          {error}
        </p> :
      null}
    </div>);

}

interface TextFieldProps extends BaseProps {
  value: string;
  onChange: (value: string) => void;
  type?: string;
  placeholder?: string;
  autoComplete?: string;
  max?: string;
  min?: string;
  readOnly?: boolean;
  disabled?: boolean;
  inputMode?: 'text' | 'numeric' | 'tel' | 'email' | 'decimal' | 'search' | 'url' | 'none';
}

export function TextField({
  id,
  label,
  hint,
  error,
  required,
  value,
  onChange,
  type = 'text',
  placeholder,
  autoComplete,
  max,
  min,
  readOnly,
  disabled,
  inputMode
}: TextFieldProps) {
  return (
    <Field id={id} label={label} hint={hint} error={error} required={required}>
      <input
        id={id}
        className={`input${error ? ' input--error' : ''}`}
        type={type}
        value={value}
        placeholder={placeholder}
        autoComplete={autoComplete}
        max={max}
        min={min}
        readOnly={readOnly}
        disabled={disabled}
        inputMode={inputMode}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
        onChange={(e) => onChange(e.target.value)} />
      
    </Field>);

}

interface TextAreaFieldProps extends BaseProps {
  value: string;
  onChange: (value: string) => void;
  rows?: number;
  placeholder?: string;
  points?: string[];
}

export function TextAreaField({
  id,
  label,
  hint,
  error,
  required,
  value,
  onChange,
  rows = 4,
  placeholder,
  points
}: TextAreaFieldProps) {
  return (
    <div className="field">
      <label className="field-label" htmlFor={id}>
        {label}
        {required ? <span className="req" aria-hidden="true">*</span> : null}
      </label>
      {hint ?
      <p className="field-hint" id={`${id}-hint`}>
          {hint}
        </p> :
      null}
      {points ?
      <ul className="helper-list">
          {points.map((point) =>
        <li key={point}>{point}</li>
        )}
        </ul> :
      null}
      <textarea
        id={id}
        className={`textarea${error ? ' textarea--error' : ''}`}
        rows={rows}
        value={value}
        placeholder={placeholder}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
        onChange={(e) => onChange(e.target.value)} />
      
      {error ?
      <p className="error-text" id={`${id}-error`} role="alert">
          <AlertCircleIcon size={13} aria-hidden="true" />
          {error}
        </p> :
      null}
    </div>);

}